/**
 * Configuration Karaté Phase 1 — fondation DojoOS / MartialCore.
 */
import { resolveKarateShellMode } from "@/config/karateShellMode.js";
import { createDojoOsAppConfig } from "@/coquille-bridge/CoquilleMartialCoreBridge.js";
import {
  LEGACY_ADMIN_SHOTOKAN_PATHS,
  LEGACY_SENSEI_SHOTOKAN_PATHS,
  LEGACY_SEMPAI_SHOTOKAN_PATHS,
  ADMIN_SHOTOKAN_BASE,
  SENSEI_SHOTOKAN_BASE,
  SEMPAI_SHOTOKAN_BASE,
} from "@/pages/shotokanBlackDragon/shotokanNavConfig.js";
import { LEGACY_DOCUMENTS_PREUVES_ADMIN_PATHS } from "@/pages/documentsPreuves/documentsPreuvesNavConfig.js";

export const karateAppConfig = createDojoOsAppConfig({
  appName: "Shotokan AAMC",
  appShortName: "Karaté",
  mode: resolveKarateShellMode(),
  enabledRoles: ["admin", "manager", "assistant", "player", "parent", "federation"],
  enabledModules: [
    "dashboard",
    "organizations",
    "dojos",
    "instructors",
    "assistants",
    "students",
    "parents",
    "classes",
    "schedule",
    "attendance",
    "belts",
    "progress",
    "exams",
    "events",
    "competitions",
    "documents",
    "communications",
    "billing",
    "reports",
    "settings",
    "help",
    "training",
  ],
  roleIdMap: {
    admin: "hanshi",
    manager: "sensei",
    player: "student",
  },
  roleAliases: {
    admin: ["hanshi", "admin"],
    manager: ["sensei"],
    player: ["student"],
    assistant: ["assistant"],
    parent: ["parent"],
  },
  phaseLabel: { fr: "", en: "" },
  demoBanner: {
    fr: "",
    en: "",
  },
  routes: {
    "admin.organizations": "/admin/organisation",
    "admin.attendance": "/admin/presences",
    "assistant.dashboard": "/sempai",
    "assistant.classes": "/sempai/classes",
    "assistant.students": "/sempai/etudiants",
    "assistant.documents": "/sempai/documents",
    "manager.attendance": "/sensei/presences",
    "parent.dashboard": "/parent",
    "parent.children": "/parent/mes-enfants",
    "parent.schedule": "/parent/horaires",
    "parent.progress": "/parent/progression",
    "parent.events": "/parent/evenements",
    "parent.communications": "/parent/communications",
    "parent.documents": "/parent/documents",
    "player.events": "/mes-evenements",
    "player.classes": "/mes-cours",
    "player.schedule": "/mes-cours",
    "manager.dojos": "/sensei/mon-dojo",
    "admin.progress": "/admin/progression",
    "admin.exams": "/admin/examens",
    "assistant.progress": "/sempai/progression",
    "assistant.belts": "/sempai/ceintures",
    "manager.belts": "/sensei/ceintures",
    "admin.titles": "/admin/shotokan-black-dragon/titres",
    "admin.titleApplications": "/admin/shotokan-black-dragon/demandes",
    "admin.recognitions": "/admin/shotokan-black-dragon/reconnaissances",
    "admin.titleAuthority": "/admin/shotokan-black-dragon/autorite",
    "admin.shotokanBlackDragon": "/admin/shotokan-black-dragon",
    "admin.documentsPreuves": "/admin/documents-preuves",
    "manager.titles": "/sensei/shotokan-black-dragon/titres",
    "manager.titleApplications": "/sensei/shotokan-black-dragon/demandes",
    "manager.shotokanBlackDragon": "/sensei/shotokan-black-dragon",
    "assistant.titles": "/sempai/shotokan-black-dragon/parcours",
    "assistant.shotokanBlackDragon": "/sempai/shotokan-black-dragon",
    "player.titles": "/mes-titres",
    "public.contact": "/contact",
    "public.privacy": "/confidentialite",
    "admin.instructors": "/hanshi/instructeurs",
    "admin.students": "/hanshi/membres",
    "manager.students": "/sensei/etudiants",
  },
});

const adminGroup = karateAppConfig.navGroups.find((g) => g.id === "admin");
if (adminGroup) {
  const withoutLaunch = adminGroup.items.filter((i) => i.to !== "/admin/lancement-1er-septembre" && i.id !== "admin-launch-cockpit");
  adminGroup.items = withoutLaunch;
}
if (adminGroup && !adminGroup.items.some((i) => i.to === "/accueil")) {
  adminGroup.items.unshift({
    id: "connected-home",
    to: "/accueil",
    labelFr: "Accueil",
    labelEn: "Home",
    icon: "🏠",
    moduleId: "dashboard",
    phase: "phase1",
    end: true,
  });
}
if (adminGroup && !adminGroup.items.some((i) => i.to === "/admin/adhesions")) {
  adminGroup.items.push(
    {
      id: "admin-adhesions",
      to: "/admin/adhesions",
      labelFr: "Adhésions",
      labelEn: "Memberships",
      icon: "🪪",
      phase: "phase1",
    },
    {
      id: "admin-membres-officiels",
      to: "/admin/membres-officiels",
      labelFr: "Membres officiels",
      labelEn: "Official members",
      icon: "🥋",
      phase: "phase1",
    },
    {
      id: "admin-bd-adhesions",
      to: "/admin/black-dragon-adhesions",
      labelFr: "Adhésions Black Dragon",
      labelEn: "Black Dragon memberships",
      icon: "🐉",
      phase: "phase1",
    },
  );
}
if (adminGroup && !adminGroup.items.some((i) => i.to === "/admin/diagnostic-avatar")) {
  adminGroup.items.push({
    id: "admin-diagnostic-avatar",
    to: "/admin/diagnostic-avatar",
    labelFr: "Diagnostic avatar",
    labelEn: "Avatar diagnostic",
    icon: "🩺",
    moduleId: "settings",
    phase: "phase1",
  });
}
if (adminGroup && !adminGroup.items.some((i) => i.to === "/admin/presences")) {
  adminGroup.items.push({
    id: "admin-attendance",
    to: "/admin/presences",
    labelFr: "Présences légères",
    labelEn: "Light attendance",
    icon: "✓",
    moduleId: "attendance",
    phase: "phase1",
  });
}
if (adminGroup && !adminGroup.items.some((i) => i.to === "/admin/progression")) {
  adminGroup.items.push({
    id: "admin-progression",
    to: "/admin/progression",
    labelFr: "Progression",
    labelEn: "Progress",
    icon: "📈",
    moduleId: "progress",
    phase: "phase1",
  });
}
if (adminGroup && !adminGroup.items.some((i) => i.to === "/admin/examens")) {
  adminGroup.items.push({
    id: "admin-examens",
    to: "/admin/examens",
    labelFr: "Examens légers",
    labelEn: "Light exams",
    icon: "📝",
    moduleId: "exams",
    phase: "phase1",
  });
}
const adminBeltsItem = adminGroup?.items.find((i) => i.moduleId === "belts");
if (adminBeltsItem) adminBeltsItem.phase = "phase1";

if (adminGroup) {
  adminGroup.items = adminGroup.items.filter(
    (i) => !LEGACY_ADMIN_SHOTOKAN_PATHS.includes(i.to) && !LEGACY_DOCUMENTS_PREUVES_ADMIN_PATHS.includes(i.to),
  );
  if (!adminGroup.items.some((i) => i.to === ADMIN_SHOTOKAN_BASE)) {
    adminGroup.items.push({
      id: "admin-shotokan-black-dragon",
      to: ADMIN_SHOTOKAN_BASE,
      labelFr: "Shotokan Black Dragon",
      labelEn: "Shotokan Black Dragon",
      icon: "🐉",
      phase: "phase1",
    });
  }
}

const assistantGroup = karateAppConfig.navGroups.find((g) => g.id === "assistant");
if (assistantGroup) {
  const attItem = assistantGroup.items.find((i) => i.moduleId === "attendance");
  if (attItem) attItem.to = "/sempai/presences";
  if (!assistantGroup.items.some((i) => i.to === "/sempai/evenements")) {
    assistantGroup.items.push({
      id: "assistant-events",
      to: "/sempai/evenements",
      labelFr: "Événements",
      labelEn: "Events",
      icon: "📅",
      moduleId: "events",
      phase: "phase1",
    });
  }
  if (!assistantGroup.items.some((i) => i.to === "/sempai/progression")) {
    assistantGroup.items.push({
      id: "assistant-progression",
      to: "/sempai/progression",
      labelFr: "Progression",
      labelEn: "Progress",
      icon: "📈",
      moduleId: "progress",
      phase: "phase1",
    });
  }
  if (!assistantGroup.items.some((i) => i.to === "/sempai/ceintures")) {
    assistantGroup.items.push({
      id: "assistant-ceintures",
      to: "/sempai/ceintures",
      labelFr: "Ceintures",
      labelEn: "Belts",
      icon: "🎗️",
      moduleId: "belts",
      phase: "phase1",
    });
  }
  assistantGroup.items = assistantGroup.items.filter((i) => !LEGACY_SEMPAI_SHOTOKAN_PATHS.includes(i.to));
  if (!assistantGroup.items.some((i) => i.to === SEMPAI_SHOTOKAN_BASE)) {
    assistantGroup.items.push({
      id: "assistant-shotokan-black-dragon",
      to: SEMPAI_SHOTOKAN_BASE,
      labelFr: "Shotokan Black Dragon",
      labelEn: "Shotokan Black Dragon",
      icon: "🐉",
      phase: "phase1",
    });
  }
}

const managerGroup = karateAppConfig.navGroups.find((g) => g.id === "sensei");
if (managerGroup) {
  const beltsItem = managerGroup.items.find((i) => i.moduleId === "belts");
  if (beltsItem) beltsItem.to = "/sensei/ceintures";
  const progressItem = managerGroup.items.find((i) => i.moduleId === "progress");
  if (progressItem) progressItem.phase = "phase1";
  const examsItem = managerGroup.items.find((i) => i.moduleId === "exams");
  if (examsItem) {
    examsItem.phase = "phase1";
    examsItem.labelFr = "Examens légers";
    examsItem.labelEn = "Light exams";
  }
  managerGroup.items = managerGroup.items.filter((i) => !LEGACY_SENSEI_SHOTOKAN_PATHS.includes(i.to));
  if (!managerGroup.items.some((i) => i.to === SENSEI_SHOTOKAN_BASE)) {
    managerGroup.items.push({
      id: "manager-shotokan-black-dragon",
      to: SENSEI_SHOTOKAN_BASE,
      labelFr: "Shotokan Black Dragon",
      labelEn: "Shotokan Black Dragon",
      icon: "🐉",
      phase: "phase1",
    });
  }
}

const studentGroup = karateAppConfig.navGroups.find((g) => g.id === "student");
if (studentGroup) {
  const eventsItem = studentGroup.items.find((i) => i.moduleId === "events");
  if (eventsItem) eventsItem.to = "/mes-evenements";
  if (!studentGroup.items.some((i) => i.to === "/mon-adhesion")) {
    studentGroup.items.push(
      { id: "student-adhesion", to: "/mon-adhesion", labelFr: "Mon adhésion", labelEn: "My membership", icon: "🪪", phase: "phase1" },
      { id: "student-calendar", to: "/mon-calendrier", labelFr: "Mon calendrier", labelEn: "My calendar", icon: "🗓️", phase: "phase1" },
      { id: "student-exams", to: "/mes-examens", labelFr: "Mes examens", labelEn: "My exams", icon: "📝", phase: "phase1" },
      { id: "student-bd", to: "/espace-black-dragon", labelFr: "Black Dragon", labelEn: "Black Dragon", icon: "🐉", phase: "phase1" },
      { id: "student-titres", to: "/mes-titres", labelFr: "Mes titres", labelEn: "My titles", icon: "🏅", phase: "phase1" },
    );
  }
}

// Alias legacy admin role for shell badges
if (!karateAppConfig.roles.some((r) => r.id === "admin")) {
  karateAppConfig.roles.push({
    id: "admin",
    dojoOsId: "admin",
    labelFr: "Administration",
    labelEn: "Administration",
    icon: "⚙️",
    availability: "active",
  });
}

if (adminGroup && !adminGroup.items.some((i) => i.to === "/admin/presentation-interactive")) {
  adminGroup.items.push({
    id: "admin-presentation-interactive",
    to: "/admin/presentation-interactive",
    labelFr: "Présentation interactive",
    labelEn: "Interactive presentation",
    icon: "🎯",
    phase: "phase1",
  });
}

const instructorsNavItem = adminGroup?.items.find((i) => i.moduleId === "instructors" || i.id === "instructors");
if (instructorsNavItem) {
  instructorsNavItem.to = "/hanshi/instructeurs";
}

if (adminGroup && !adminGroup.items.some((i) => i.to === "/admin/preparation-liste-officielle")) {
  adminGroup.items.push({
    id: "admin-official-list-prep",
    to: "/admin/preparation-liste-officielle",
    labelFr: "Préparation liste officielle",
    labelEn: "Official List Preparation",
    icon: "📋",
    phase: "phase1",
  });
}

const publicGroup = karateAppConfig.navGroups.find((g) => g.id === "public");
if (publicGroup) {
  if (!publicGroup.items.some((i) => i.to === "/contact")) {
    publicGroup.items.push(
      { id: "contact", to: "/contact", labelFr: "Contact", labelEn: "Contact", icon: "📞" },
      { id: "privacy", to: "/confidentialite", labelFr: "Confidentialité", labelEn: "Privacy", icon: "🔒" },
    );
  }
  if (!publicGroup.items.some((i) => i.to === "/devenir-membre")) {
    publicGroup.items.unshift(
      { id: "become-member", to: "/devenir-membre", labelFr: "Devenir membre", labelEn: "Become a member", icon: "🥋" },
    );
  }
  if (!publicGroup.items.some((i) => i.to === "/calendrier")) {
    publicGroup.items.push(
      { id: "public-calendar", to: "/calendrier", labelFr: "Calendrier", labelEn: "Calendar", icon: "🗓️" },
      { id: "public-black-dragon", to: "/black-dragon", labelFr: "Black Dragon", labelEn: "Black Dragon", icon: "🐉" },
    );
  }
}
